import { useQuery } from '@apollo/client';
import { SimpleGrid } from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import {
  Control,
  FieldError,
  FieldValues,
  FormState,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { GET_PAGES } from '../../../graphQL/queries';
import redirectTypes, { RedirectValues } from '../../../utils/redirectTypes';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import DynamicFields from '../DynamicFields';

export const redirectFormSchema = yup.object().shape({
  redirect_value: yup
    .mixed()
    .test(
      'redirect_value',
      'Adicione um valor de redirecionamento',
      (value: any): any => {
        if (typeof value === 'string') {
          if (!value) {
            return false;
          }
          return true;
        }
        if (value?.value) {
          return true;
        }
        return false;
      },
    ),
  redirect_type: yup
    .object()
    .shape({
      value: yup.string().required('Escolha uma forma de redirecionamento'),
      label: yup.string().required('Escolha uma forma de redirecionamento'),
    })
    .required('Escolha uma forma de redirecionamento'),
});

type PageElement = {
  redirect_type: RedirectValues;
  redirect_value: string;
  redirect_data: any;
  facets: any;
};

type RedirectFormProps<T extends FieldValues = any> = {
  register: UseFormRegister<T>;
  control: Control;
  formState: FormState<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  pageElement?: PageElement;
};

const RedirectForm: FC<RedirectFormProps> = ({
  control,
  formState,
  register,
  setValue,
  pageElement,
}) => {
  const [typeRedirect, setTypeRedirect] = useState<RedirectValues>(
    pageElement?.redirect_type ?? 'product',
  );

  const { data: pagesReturn } = useQuery(GET_PAGES, {
    variables: {
      limit: 0,
      page: 1,
    },
  });

  const pages = pagesReturn?.pagesPaginated?.data;

  const { errors } = formState;

  useEffect(() => {
    if (!pageElement) {
      setValue('redirect_type', redirectTypes[0]);
      return;
    }

    const { redirect_type, redirect_value, facets } = pageElement;
    setValue('facets', facets);
    setValue('redirect_value', redirect_value);
    setValue(
      'redirect_type',
      redirectTypes.find(type => type?.value === redirect_type),
    );
    setValue('redirect_data', pageElement?.redirect_data ?? undefined);
    if (redirect_type === 'app_screen') {
      setTypeRedirect('app_screen');
      const selectedValueSelect = pages?.find(
        (page: any) => page.slug === redirect_value,
      );
      setValue('redirect_value', {
        label: selectedValueSelect?.name,
        value: selectedValueSelect?.slug,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageElement]);
  const { t } = useTranslation();
  return (
    <SimpleGrid columns={2} spacing="3" w="full">
      <SimpleGrid spacing="4">
        <Select
          name="redirect_type"
          control={control}
          label={t('redirectField')}
          error={errors.redirect_type}
          options={redirectTypes}
          defaultValue={redirectTypes[0]}
          onChange={(e: any) => {
            setTypeRedirect(e?.value);
            setValue('redirect_value', '');
          }}
        />
        {typeRedirect === 'app_screen_custom' && (
          <DynamicFields
            title={t('redirectValue')}
            defaultValue={
              pageElement?.redirect_data ?? [{ key: '', value: '' }]
            }
            onUpdate={data => {
              setValue('redirect_data', data);
            }}
          />
        )}
      </SimpleGrid>
      {typeRedirect === 'query' && (
        <Input
          label={t('facetsField')}
          w="100%"
          error={errors.facets as FieldError | undefined}
          {...register('facets')}
        />
      )}
      {typeRedirect !== 'app_screen' ? (
        <Input
          label={t('redirectValueField')}
          error={errors.redirect_value as FieldError | undefined}
          {...register('redirect_value')}
        />
      ) : (
        <Select
          name="redirect_value"
          control={control}
          label={t('redirectValueField')}
          error={errors.redirect_value}
          options={pages?.map((page: any) => {
            return {
              label: page?.name,
              value: page?.slug,
            };
          })}
        />
      )}
    </SimpleGrid>
  );
};

export default RedirectForm;
